/**
 * Authentication Error Message Translations
 * DEV-267: Low Priority - i18n support for error messages
 *
 * This file provides localized error messages for all authentication error codes.
 * New locales can be added by creating a new entry in AUTH_ERROR_TRANSLATIONS.
 *
 * Supported locales:
 * - en: English (default)
 * - es: Spanish
 * - de: German
 * - fr: French
 * - zh: Chinese (Simplified)
 * - ja: Japanese
 */

import { AuthErrorCode } from '../models/auth-errors.model';

/**
 * Supported locale codes
 */
export type AuthErrorLocale = 'en' | 'es' | 'de' | 'fr' | 'zh' | 'ja';

/**
 * Structure for error message with optional recovery guidance
 */
export interface LocalizedAuthError {
  message: string;
  recoveryGuidance?: string;
}

/**
 * Translation map type
 */
export type AuthErrorTranslationMap = Record<AuthErrorCode, LocalizedAuthError>;

/**
 * Default locale
 */
export const DEFAULT_AUTH_ERROR_LOCALE: AuthErrorLocale = 'en';

/**
 * English (Default) translations
 */
const EN_TRANSLATIONS: AuthErrorTranslationMap = {
  // Credential errors
  [AuthErrorCode.INVALID_CREDENTIALS]: {
    message: 'Invalid email or password. Please check your credentials and try again.',
    recoveryGuidance: 'Make sure Caps Lock is off and try again.'
  },
  [AuthErrorCode.INVALID_EMAIL]: {
    message: 'Please enter a valid email address.',
    recoveryGuidance: 'Check that your email address is formatted correctly (e.g., user@example.com).'
  },
  [AuthErrorCode.INVALID_PASSWORD]: {
    message: 'Invalid password. Please try again.',
    recoveryGuidance: 'Make sure Caps Lock is off and try again.'
  },
  [AuthErrorCode.WEAK_PASSWORD]: {
    message: 'Password is too weak. Please use a stronger password.',
    recoveryGuidance: 'Use at least 8 characters with uppercase, lowercase, numbers, and special characters.'
  },

  // Account state errors
  [AuthErrorCode.ACCOUNT_LOCKED]: {
    message: 'Your account has been temporarily locked due to multiple failed login attempts.',
    recoveryGuidance: 'Please try again later or contact support.'
  },
  [AuthErrorCode.ACCOUNT_DISABLED]: {
    message: 'Your account has been disabled.',
    recoveryGuidance: 'Please contact support for assistance.'
  },
  [AuthErrorCode.ACCOUNT_NOT_FOUND]: {
    message: 'Invalid email or password. Please check your credentials and try again as these are not found.',
    recoveryGuidance: 'If you don\'t have an account, please register.'
  },
  [AuthErrorCode.ACCOUNT_NOT_VERIFIED]: {
    message: 'Please verify your email address before logging in.',
    recoveryGuidance: 'Check your inbox for the verification link.'
  },
  [AuthErrorCode.EMAIL_ALREADY_EXISTS]: {
    message: 'An account with this email already exists.',
    recoveryGuidance: 'Please try logging in or use a different email.'
  },
  [AuthErrorCode.USERNAME_ALREADY_EXISTS]: {
    message: 'This username is already taken.',
    recoveryGuidance: 'Please choose a different username.'
  },

  // MFA errors
  [AuthErrorCode.MFA_REQUIRED]: {
    message: 'Multi-factor authentication is required.',
    recoveryGuidance: 'Please enter your verification code.'
  },
  [AuthErrorCode.MFA_INVALID_CODE]: {
    message: 'Invalid verification code. Please check the code and try again.',
    recoveryGuidance: 'Make sure you\'re using the latest code from your authenticator app.'
  },
  [AuthErrorCode.MFA_CODE_EXPIRED]: {
    message: 'Verification code has expired.',
    recoveryGuidance: 'Please request a new code.'
  },
  [AuthErrorCode.MFA_TOO_MANY_ATTEMPTS]: {
    message: 'Too many verification attempts. Please wait before trying again.',
    recoveryGuidance: 'Wait 5 minutes before requesting a new code.'
  },
  [AuthErrorCode.MFA_NOT_CONFIGURED]: {
    message: 'Multi-factor authentication is not configured for your account.',
    recoveryGuidance: 'Please set up MFA in your account settings.'
  },
  [AuthErrorCode.BACKUP_CODE_INVALID]: {
    message: 'Invalid backup code.',
    recoveryGuidance: 'Please check the code and try again, or use your authenticator app.'
  },

  // Session/Token errors
  [AuthErrorCode.TOKEN_EXPIRED]: {
    message: 'Your session has expired.',
    recoveryGuidance: 'Please log in again to continue.'
  },
  [AuthErrorCode.TOKEN_INVALID]: {
    message: 'Invalid authentication token.',
    recoveryGuidance: 'Please log in again.'
  },
  [AuthErrorCode.SESSION_EXPIRED]: {
    message: 'Your session has expired.',
    recoveryGuidance: 'Please log in again to continue.'
  },
  [AuthErrorCode.REFRESH_TOKEN_EXPIRED]: {
    message: 'Your session has expired.',
    recoveryGuidance: 'Please log in again to continue.'
  },

  // Rate limiting
  [AuthErrorCode.RATE_LIMIT_EXCEEDED]: {
    message: 'Too many requests. Please wait before trying again.',
    recoveryGuidance: 'Wait a few seconds before retrying.'
  },
  [AuthErrorCode.TOO_MANY_ATTEMPTS]: {
    message: 'Too many failed attempts. Please wait before trying again.',
    recoveryGuidance: 'Wait 5 minutes before trying again.'
  },

  // Network/Server errors
  [AuthErrorCode.NETWORK_ERROR]: {
    message: 'Network error. Please check your internet connection.',
    recoveryGuidance: 'Check your connection and try again.'
  },
  [AuthErrorCode.SERVER_ERROR]: {
    message: 'Server error. Please try again later.',
    recoveryGuidance: 'Our team has been notified. Please try again in a few minutes.'
  },
  [AuthErrorCode.SERVICE_UNAVAILABLE]: {
    message: 'Service temporarily unavailable.',
    recoveryGuidance: 'Please try again in a few minutes.'
  },
  [AuthErrorCode.TIMEOUT]: {
    message: 'Request timed out. Please try again.',
    recoveryGuidance: 'Check your connection and try again.'
  },

  // Validation errors
  [AuthErrorCode.VALIDATION_ERROR]: {
    message: 'Validation error. Please check your input.',
    recoveryGuidance: 'Review the highlighted fields and correct any errors.'
  },
  [AuthErrorCode.INVALID_INPUT]: {
    message: 'Invalid input. Please check your data.',
    recoveryGuidance: 'Review your input and try again.'
  },

  // Password reset
  [AuthErrorCode.RESET_TOKEN_EXPIRED]: {
    message: 'Password reset link has expired.',
    recoveryGuidance: 'Please request a new password reset link.'
  },
  [AuthErrorCode.RESET_TOKEN_INVALID]: {
    message: 'Invalid password reset link.',
    recoveryGuidance: 'Please request a new password reset link.'
  },
  [AuthErrorCode.PASSWORD_RECENTLY_USED]: {
    message: 'This password was recently used. Please choose a different password.',
    recoveryGuidance: 'For security, you cannot reuse recent passwords.'
  },

  // Generic
  [AuthErrorCode.UNKNOWN]: {
    message: 'An unexpected error occurred.',
    recoveryGuidance: 'Please try again. If the problem persists, contact support.'
  },
  [AuthErrorCode.PERMISSION_DENIED]: {
    message: 'You do not have permission to perform this action.',
    recoveryGuidance: 'Contact your administrator if you believe this is an error.'
  }
};

/**
 * Spanish translations
 */
const ES_TRANSLATIONS: AuthErrorTranslationMap = {
  [AuthErrorCode.INVALID_CREDENTIALS]: {
    message: 'Correo electrónico o contraseña inválidos.',
    recoveryGuidance: 'Asegúrese de que Bloq Mayús esté desactivado e intente de nuevo.'
  },
  [AuthErrorCode.INVALID_EMAIL]: {
    message: 'Por favor, introduzca una dirección de correo electrónico válida.'
  },
  [AuthErrorCode.INVALID_PASSWORD]: {
    message: 'Contraseña inválida. Por favor, intente de nuevo.'
  },
  [AuthErrorCode.WEAK_PASSWORD]: {
    message: 'La contraseña es demasiado débil.',
    recoveryGuidance: 'Use al menos 8 caracteres con mayúsculas, minúsculas, números y caracteres especiales.'
  },
  [AuthErrorCode.ACCOUNT_LOCKED]: {
    message: 'Su cuenta ha sido bloqueada temporalmente.',
    recoveryGuidance: 'Por favor, intente más tarde o contacte a soporte.'
  },
  [AuthErrorCode.ACCOUNT_DISABLED]: {
    message: 'Su cuenta ha sido deshabilitada.',
    recoveryGuidance: 'Por favor, contacte a soporte para asistencia.'
  },
  [AuthErrorCode.ACCOUNT_NOT_FOUND]: {
    message: 'Correo electrónico o contraseña inválidos.'
  },
  [AuthErrorCode.ACCOUNT_NOT_VERIFIED]: {
    message: 'Por favor, verifique su correo electrónico antes de iniciar sesión.',
    recoveryGuidance: 'Revise su bandeja de entrada para el enlace de verificación.'
  },
  [AuthErrorCode.EMAIL_ALREADY_EXISTS]: {
    message: 'Ya existe una cuenta con este correo electrónico.',
    recoveryGuidance: 'Por favor, inicie sesión o use un correo diferente.'
  },
  [AuthErrorCode.USERNAME_ALREADY_EXISTS]: {
    message: 'Este nombre de usuario ya está en uso.',
    recoveryGuidance: 'Por favor, elija un nombre de usuario diferente.'
  },
  [AuthErrorCode.MFA_REQUIRED]: {
    message: 'Se requiere autenticación de múltiples factores.',
    recoveryGuidance: 'Por favor, ingrese su código de verificación.'
  },
  [AuthErrorCode.MFA_INVALID_CODE]: {
    message: 'Código de verificación inválido.',
    recoveryGuidance: 'Asegúrese de usar el código más reciente de su aplicación.'
  },
  [AuthErrorCode.MFA_CODE_EXPIRED]: {
    message: 'El código de verificación ha expirado.',
    recoveryGuidance: 'Por favor, solicite un nuevo código.'
  },
  [AuthErrorCode.MFA_TOO_MANY_ATTEMPTS]: {
    message: 'Demasiados intentos de verificación.',
    recoveryGuidance: 'Espere 5 minutos antes de solicitar un nuevo código.'
  },
  [AuthErrorCode.MFA_NOT_CONFIGURED]: {
    message: 'La autenticación de múltiples factores no está configurada.',
    recoveryGuidance: 'Por favor, configure MFA en la configuración de su cuenta.'
  },
  [AuthErrorCode.BACKUP_CODE_INVALID]: {
    message: 'Código de respaldo inválido.',
    recoveryGuidance: 'Por favor, verifique el código e intente de nuevo.'
  },
  [AuthErrorCode.TOKEN_EXPIRED]: {
    message: 'Su sesión ha expirado.',
    recoveryGuidance: 'Por favor, inicie sesión de nuevo para continuar.'
  },
  [AuthErrorCode.TOKEN_INVALID]: {
    message: 'Token de autenticación inválido.',
    recoveryGuidance: 'Por favor, inicie sesión de nuevo.'
  },
  [AuthErrorCode.SESSION_EXPIRED]: {
    message: 'Su sesión ha expirado.',
    recoveryGuidance: 'Por favor, inicie sesión de nuevo para continuar.'
  },
  [AuthErrorCode.REFRESH_TOKEN_EXPIRED]: {
    message: 'Su sesión ha expirado.',
    recoveryGuidance: 'Por favor, inicie sesión de nuevo para continuar.'
  },
  [AuthErrorCode.RATE_LIMIT_EXCEEDED]: {
    message: 'Demasiadas solicitudes. Por favor, espere.',
    recoveryGuidance: 'Espere unos segundos antes de reintentar.'
  },
  [AuthErrorCode.TOO_MANY_ATTEMPTS]: {
    message: 'Demasiados intentos fallidos.',
    recoveryGuidance: 'Espere 5 minutos antes de intentar de nuevo.'
  },
  [AuthErrorCode.NETWORK_ERROR]: {
    message: 'Error de red. Por favor, verifique su conexión a internet.',
    recoveryGuidance: 'Verifique su conexión e intente de nuevo.'
  },
  [AuthErrorCode.SERVER_ERROR]: {
    message: 'Error del servidor. Por favor, intente más tarde.',
    recoveryGuidance: 'Nuestro equipo ha sido notificado.'
  },
  [AuthErrorCode.SERVICE_UNAVAILABLE]: {
    message: 'Servicio temporalmente no disponible.',
    recoveryGuidance: 'Por favor, intente de nuevo en unos minutos.'
  },
  [AuthErrorCode.TIMEOUT]: {
    message: 'La solicitud ha expirado. Por favor, intente de nuevo.',
    recoveryGuidance: 'Verifique su conexión e intente de nuevo.'
  },
  [AuthErrorCode.VALIDATION_ERROR]: {
    message: 'Error de validación. Por favor, verifique su entrada.',
    recoveryGuidance: 'Revise los campos resaltados y corrija los errores.'
  },
  [AuthErrorCode.INVALID_INPUT]: {
    message: 'Entrada inválida. Por favor, verifique sus datos.',
    recoveryGuidance: 'Revise su entrada e intente de nuevo.'
  },
  [AuthErrorCode.RESET_TOKEN_EXPIRED]: {
    message: 'El enlace de restablecimiento de contraseña ha expirado.',
    recoveryGuidance: 'Por favor, solicite un nuevo enlace.'
  },
  [AuthErrorCode.RESET_TOKEN_INVALID]: {
    message: 'Enlace de restablecimiento de contraseña inválido.',
    recoveryGuidance: 'Por favor, solicite un nuevo enlace.'
  },
  [AuthErrorCode.PASSWORD_RECENTLY_USED]: {
    message: 'Esta contraseña fue usada recientemente.',
    recoveryGuidance: 'Por seguridad, no puede reusar contraseñas recientes.'
  },
  [AuthErrorCode.UNKNOWN]: {
    message: 'Ocurrió un error inesperado.',
    recoveryGuidance: 'Por favor, intente de nuevo. Si el problema persiste, contacte a soporte.'
  },
  [AuthErrorCode.PERMISSION_DENIED]: {
    message: 'No tiene permiso para realizar esta acción.',
    recoveryGuidance: 'Contacte a su administrador si cree que esto es un error.'
  }
};

/**
 * German translations
 */
const DE_TRANSLATIONS: AuthErrorTranslationMap = {
  [AuthErrorCode.INVALID_CREDENTIALS]: {
    message: 'Ungültige E-Mail oder Passwort.',
    recoveryGuidance: 'Stellen Sie sicher, dass die Feststelltaste ausgeschaltet ist.'
  },
  [AuthErrorCode.INVALID_EMAIL]: {
    message: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.'
  },
  [AuthErrorCode.INVALID_PASSWORD]: {
    message: 'Ungültiges Passwort. Bitte versuchen Sie es erneut.'
  },
  [AuthErrorCode.WEAK_PASSWORD]: {
    message: 'Passwort ist zu schwach.',
    recoveryGuidance: 'Verwenden Sie mindestens 8 Zeichen mit Groß-, Kleinbuchstaben, Zahlen und Sonderzeichen.'
  },
  [AuthErrorCode.ACCOUNT_LOCKED]: {
    message: 'Ihr Konto wurde vorübergehend gesperrt.',
    recoveryGuidance: 'Bitte versuchen Sie es später erneut oder kontaktieren Sie den Support.'
  },
  [AuthErrorCode.ACCOUNT_DISABLED]: {
    message: 'Ihr Konto wurde deaktiviert.',
    recoveryGuidance: 'Bitte kontaktieren Sie den Support für Hilfe.'
  },
  [AuthErrorCode.ACCOUNT_NOT_FOUND]: {
    message: 'Ungültige E-Mail oder Passwort.'
  },
  [AuthErrorCode.ACCOUNT_NOT_VERIFIED]: {
    message: 'Bitte verifizieren Sie Ihre E-Mail-Adresse.',
    recoveryGuidance: 'Überprüfen Sie Ihren Posteingang auf den Bestätigungslink.'
  },
  [AuthErrorCode.EMAIL_ALREADY_EXISTS]: {
    message: 'Ein Konto mit dieser E-Mail existiert bereits.',
    recoveryGuidance: 'Bitte melden Sie sich an oder verwenden Sie eine andere E-Mail.'
  },
  [AuthErrorCode.USERNAME_ALREADY_EXISTS]: {
    message: 'Dieser Benutzername ist bereits vergeben.',
    recoveryGuidance: 'Bitte wählen Sie einen anderen Benutzernamen.'
  },
  [AuthErrorCode.MFA_REQUIRED]: {
    message: 'Multi-Faktor-Authentifizierung erforderlich.',
    recoveryGuidance: 'Bitte geben Sie Ihren Verifizierungscode ein.'
  },
  [AuthErrorCode.MFA_INVALID_CODE]: {
    message: 'Ungültiger Verifizierungscode.',
    recoveryGuidance: 'Stellen Sie sicher, dass Sie den neuesten Code verwenden.'
  },
  [AuthErrorCode.MFA_CODE_EXPIRED]: {
    message: 'Verifizierungscode ist abgelaufen.',
    recoveryGuidance: 'Bitte fordern Sie einen neuen Code an.'
  },
  [AuthErrorCode.MFA_TOO_MANY_ATTEMPTS]: {
    message: 'Zu viele Verifizierungsversuche.',
    recoveryGuidance: 'Warten Sie 5 Minuten, bevor Sie einen neuen Code anfordern.'
  },
  [AuthErrorCode.MFA_NOT_CONFIGURED]: {
    message: 'Multi-Faktor-Authentifizierung ist nicht konfiguriert.',
    recoveryGuidance: 'Bitte richten Sie MFA in Ihren Kontoeinstellungen ein.'
  },
  [AuthErrorCode.BACKUP_CODE_INVALID]: {
    message: 'Ungültiger Backup-Code.',
    recoveryGuidance: 'Bitte überprüfen Sie den Code und versuchen Sie es erneut.'
  },
  [AuthErrorCode.TOKEN_EXPIRED]: {
    message: 'Ihre Sitzung ist abgelaufen.',
    recoveryGuidance: 'Bitte melden Sie sich erneut an, um fortzufahren.'
  },
  [AuthErrorCode.TOKEN_INVALID]: {
    message: 'Ungültiges Authentifizierungstoken.',
    recoveryGuidance: 'Bitte melden Sie sich erneut an.'
  },
  [AuthErrorCode.SESSION_EXPIRED]: {
    message: 'Ihre Sitzung ist abgelaufen.',
    recoveryGuidance: 'Bitte melden Sie sich erneut an, um fortzufahren.'
  },
  [AuthErrorCode.REFRESH_TOKEN_EXPIRED]: {
    message: 'Ihre Sitzung ist abgelaufen.',
    recoveryGuidance: 'Bitte melden Sie sich erneut an, um fortzufahren.'
  },
  [AuthErrorCode.RATE_LIMIT_EXCEEDED]: {
    message: 'Zu viele Anfragen. Bitte warten Sie.',
    recoveryGuidance: 'Warten Sie einige Sekunden, bevor Sie es erneut versuchen.'
  },
  [AuthErrorCode.TOO_MANY_ATTEMPTS]: {
    message: 'Zu viele fehlgeschlagene Versuche.',
    recoveryGuidance: 'Warten Sie 5 Minuten, bevor Sie es erneut versuchen.'
  },
  [AuthErrorCode.NETWORK_ERROR]: {
    message: 'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung.',
    recoveryGuidance: 'Überprüfen Sie Ihre Verbindung und versuchen Sie es erneut.'
  },
  [AuthErrorCode.SERVER_ERROR]: {
    message: 'Serverfehler. Bitte versuchen Sie es später erneut.',
    recoveryGuidance: 'Unser Team wurde benachrichtigt.'
  },
  [AuthErrorCode.SERVICE_UNAVAILABLE]: {
    message: 'Dienst vorübergehend nicht verfügbar.',
    recoveryGuidance: 'Bitte versuchen Sie es in einigen Minuten erneut.'
  },
  [AuthErrorCode.TIMEOUT]: {
    message: 'Anfrage abgelaufen. Bitte versuchen Sie es erneut.',
    recoveryGuidance: 'Überprüfen Sie Ihre Verbindung und versuchen Sie es erneut.'
  },
  [AuthErrorCode.VALIDATION_ERROR]: {
    message: 'Validierungsfehler. Bitte überprüfen Sie Ihre Eingabe.',
    recoveryGuidance: 'Überprüfen Sie die markierten Felder und korrigieren Sie die Fehler.'
  },
  [AuthErrorCode.INVALID_INPUT]: {
    message: 'Ungültige Eingabe. Bitte überprüfen Sie Ihre Daten.',
    recoveryGuidance: 'Überprüfen Sie Ihre Eingabe und versuchen Sie es erneut.'
  },
  [AuthErrorCode.RESET_TOKEN_EXPIRED]: {
    message: 'Passwort-Zurücksetzungslink ist abgelaufen.',
    recoveryGuidance: 'Bitte fordern Sie einen neuen Link an.'
  },
  [AuthErrorCode.RESET_TOKEN_INVALID]: {
    message: 'Ungültiger Passwort-Zurücksetzungslink.',
    recoveryGuidance: 'Bitte fordern Sie einen neuen Link an.'
  },
  [AuthErrorCode.PASSWORD_RECENTLY_USED]: {
    message: 'Dieses Passwort wurde kürzlich verwendet.',
    recoveryGuidance: 'Aus Sicherheitsgründen können Sie aktuelle Passwörter nicht wiederverwenden.'
  },
  [AuthErrorCode.UNKNOWN]: {
    message: 'Ein unerwarteter Fehler ist aufgetreten.',
    recoveryGuidance: 'Bitte versuchen Sie es erneut. Bei anhaltenden Problemen kontaktieren Sie den Support.'
  },
  [AuthErrorCode.PERMISSION_DENIED]: {
    message: 'Sie haben keine Berechtigung für diese Aktion.',
    recoveryGuidance: 'Kontaktieren Sie Ihren Administrator, wenn Sie glauben, dass dies ein Fehler ist.'
  }
};

/**
 * French translations
 */
const FR_TRANSLATIONS: AuthErrorTranslationMap = {
  [AuthErrorCode.INVALID_CREDENTIALS]: {
    message: 'Email ou mot de passe invalide.',
    recoveryGuidance: 'Assurez-vous que le verrouillage majuscule est désactivé.'
  },
  [AuthErrorCode.INVALID_EMAIL]: {
    message: 'Veuillez entrer une adresse email valide.'
  },
  [AuthErrorCode.INVALID_PASSWORD]: {
    message: 'Mot de passe invalide. Veuillez réessayer.'
  },
  [AuthErrorCode.WEAK_PASSWORD]: {
    message: 'Le mot de passe est trop faible.',
    recoveryGuidance: 'Utilisez au moins 8 caractères avec des majuscules, minuscules, chiffres et caractères spéciaux.'
  },
  [AuthErrorCode.ACCOUNT_LOCKED]: {
    message: 'Votre compte a été temporairement verrouillé.',
    recoveryGuidance: 'Veuillez réessayer plus tard ou contacter le support.'
  },
  [AuthErrorCode.ACCOUNT_DISABLED]: {
    message: 'Votre compte a été désactivé.',
    recoveryGuidance: 'Veuillez contacter le support pour assistance.'
  },
  [AuthErrorCode.ACCOUNT_NOT_FOUND]: {
    message: 'Email ou mot de passe invalide.'
  },
  [AuthErrorCode.ACCOUNT_NOT_VERIFIED]: {
    message: 'Veuillez vérifier votre adresse email avant de vous connecter.',
    recoveryGuidance: 'Vérifiez votre boîte de réception pour le lien de vérification.'
  },
  [AuthErrorCode.EMAIL_ALREADY_EXISTS]: {
    message: 'Un compte avec cet email existe déjà.',
    recoveryGuidance: 'Veuillez vous connecter ou utiliser un email différent.'
  },
  [AuthErrorCode.USERNAME_ALREADY_EXISTS]: {
    message: 'Ce nom d\'utilisateur est déjà pris.',
    recoveryGuidance: 'Veuillez choisir un nom d\'utilisateur différent.'
  },
  [AuthErrorCode.MFA_REQUIRED]: {
    message: 'L\'authentification à deux facteurs est requise.',
    recoveryGuidance: 'Veuillez entrer votre code de vérification.'
  },
  [AuthErrorCode.MFA_INVALID_CODE]: {
    message: 'Code de vérification invalide.',
    recoveryGuidance: 'Assurez-vous d\'utiliser le code le plus récent.'
  },
  [AuthErrorCode.MFA_CODE_EXPIRED]: {
    message: 'Le code de vérification a expiré.',
    recoveryGuidance: 'Veuillez demander un nouveau code.'
  },
  [AuthErrorCode.MFA_TOO_MANY_ATTEMPTS]: {
    message: 'Trop de tentatives de vérification.',
    recoveryGuidance: 'Attendez 5 minutes avant de demander un nouveau code.'
  },
  [AuthErrorCode.MFA_NOT_CONFIGURED]: {
    message: 'L\'authentification à deux facteurs n\'est pas configurée.',
    recoveryGuidance: 'Veuillez configurer MFA dans les paramètres de votre compte.'
  },
  [AuthErrorCode.BACKUP_CODE_INVALID]: {
    message: 'Code de secours invalide.',
    recoveryGuidance: 'Veuillez vérifier le code et réessayer.'
  },
  [AuthErrorCode.TOKEN_EXPIRED]: {
    message: 'Votre session a expiré.',
    recoveryGuidance: 'Veuillez vous reconnecter pour continuer.'
  },
  [AuthErrorCode.TOKEN_INVALID]: {
    message: 'Jeton d\'authentification invalide.',
    recoveryGuidance: 'Veuillez vous reconnecter.'
  },
  [AuthErrorCode.SESSION_EXPIRED]: {
    message: 'Votre session a expiré.',
    recoveryGuidance: 'Veuillez vous reconnecter pour continuer.'
  },
  [AuthErrorCode.REFRESH_TOKEN_EXPIRED]: {
    message: 'Votre session a expiré.',
    recoveryGuidance: 'Veuillez vous reconnecter pour continuer.'
  },
  [AuthErrorCode.RATE_LIMIT_EXCEEDED]: {
    message: 'Trop de requêtes. Veuillez patienter.',
    recoveryGuidance: 'Attendez quelques secondes avant de réessayer.'
  },
  [AuthErrorCode.TOO_MANY_ATTEMPTS]: {
    message: 'Trop de tentatives échouées.',
    recoveryGuidance: 'Attendez 5 minutes avant de réessayer.'
  },
  [AuthErrorCode.NETWORK_ERROR]: {
    message: 'Erreur réseau. Veuillez vérifier votre connexion internet.',
    recoveryGuidance: 'Vérifiez votre connexion et réessayez.'
  },
  [AuthErrorCode.SERVER_ERROR]: {
    message: 'Erreur serveur. Veuillez réessayer plus tard.',
    recoveryGuidance: 'Notre équipe a été notifiée.'
  },
  [AuthErrorCode.SERVICE_UNAVAILABLE]: {
    message: 'Service temporairement indisponible.',
    recoveryGuidance: 'Veuillez réessayer dans quelques minutes.'
  },
  [AuthErrorCode.TIMEOUT]: {
    message: 'La requête a expiré. Veuillez réessayer.',
    recoveryGuidance: 'Vérifiez votre connexion et réessayez.'
  },
  [AuthErrorCode.VALIDATION_ERROR]: {
    message: 'Erreur de validation. Veuillez vérifier votre saisie.',
    recoveryGuidance: 'Vérifiez les champs mis en surbrillance et corrigez les erreurs.'
  },
  [AuthErrorCode.INVALID_INPUT]: {
    message: 'Saisie invalide. Veuillez vérifier vos données.',
    recoveryGuidance: 'Vérifiez votre saisie et réessayez.'
  },
  [AuthErrorCode.RESET_TOKEN_EXPIRED]: {
    message: 'Le lien de réinitialisation du mot de passe a expiré.',
    recoveryGuidance: 'Veuillez demander un nouveau lien.'
  },
  [AuthErrorCode.RESET_TOKEN_INVALID]: {
    message: 'Lien de réinitialisation du mot de passe invalide.',
    recoveryGuidance: 'Veuillez demander un nouveau lien.'
  },
  [AuthErrorCode.PASSWORD_RECENTLY_USED]: {
    message: 'Ce mot de passe a été utilisé récemment.',
    recoveryGuidance: 'Pour des raisons de sécurité, vous ne pouvez pas réutiliser les mots de passe récents.'
  },
  [AuthErrorCode.UNKNOWN]: {
    message: 'Une erreur inattendue s\'est produite.',
    recoveryGuidance: 'Veuillez réessayer. Si le problème persiste, contactez le support.'
  },
  [AuthErrorCode.PERMISSION_DENIED]: {
    message: 'Vous n\'avez pas la permission d\'effectuer cette action.',
    recoveryGuidance: 'Contactez votre administrateur si vous pensez qu\'il s\'agit d\'une erreur.'
  }
};

/**
 * Chinese (Simplified) translations
 */
const ZH_TRANSLATIONS: AuthErrorTranslationMap = {
  [AuthErrorCode.INVALID_CREDENTIALS]: {
    message: '邮箱或密码无效。',
    recoveryGuidance: '请确保大写锁定已关闭，然后重试。'
  },
  [AuthErrorCode.INVALID_EMAIL]: {
    message: '请输入有效的电子邮件地址。'
  },
  [AuthErrorCode.INVALID_PASSWORD]: {
    message: '密码无效。请重试。'
  },
  [AuthErrorCode.WEAK_PASSWORD]: {
    message: '密码太弱。',
    recoveryGuidance: '请使用至少8个字符，包括大写、小写、数字和特殊字符。'
  },
  [AuthErrorCode.ACCOUNT_LOCKED]: {
    message: '由于多次登录失败，您的账户已被临时锁定。',
    recoveryGuidance: '请稍后重试或联系支持。'
  },
  [AuthErrorCode.ACCOUNT_DISABLED]: {
    message: '您的账户已被禁用。',
    recoveryGuidance: '请联系支持寻求帮助。'
  },
  [AuthErrorCode.ACCOUNT_NOT_FOUND]: {
    message: '邮箱或密码无效。'
  },
  [AuthErrorCode.ACCOUNT_NOT_VERIFIED]: {
    message: '登录前请先验证您的电子邮件地址。',
    recoveryGuidance: '检查您的收件箱中的验证链接。'
  },
  [AuthErrorCode.EMAIL_ALREADY_EXISTS]: {
    message: '此电子邮件已存在账户。',
    recoveryGuidance: '请尝试登录或使用其他电子邮件。'
  },
  [AuthErrorCode.USERNAME_ALREADY_EXISTS]: {
    message: '此用户名已被使用。',
    recoveryGuidance: '请选择其他用户名。'
  },
  [AuthErrorCode.MFA_REQUIRED]: {
    message: '需要多因素认证。',
    recoveryGuidance: '请输入您的验证码。'
  },
  [AuthErrorCode.MFA_INVALID_CODE]: {
    message: '验证码无效。',
    recoveryGuidance: '请确保使用最新的验证码。'
  },
  [AuthErrorCode.MFA_CODE_EXPIRED]: {
    message: '验证码已过期。',
    recoveryGuidance: '请请求新的验证码。'
  },
  [AuthErrorCode.MFA_TOO_MANY_ATTEMPTS]: {
    message: '验证尝试次数过多。',
    recoveryGuidance: '请等待5分钟后再请求新代码。'
  },
  [AuthErrorCode.MFA_NOT_CONFIGURED]: {
    message: '多因素认证未配置。',
    recoveryGuidance: '请在账户设置中设置MFA。'
  },
  [AuthErrorCode.BACKUP_CODE_INVALID]: {
    message: '备用代码无效。',
    recoveryGuidance: '请检查代码并重试。'
  },
  [AuthErrorCode.TOKEN_EXPIRED]: {
    message: '您的会话已过期。',
    recoveryGuidance: '请重新登录以继续。'
  },
  [AuthErrorCode.TOKEN_INVALID]: {
    message: '认证令牌无效。',
    recoveryGuidance: '请重新登录。'
  },
  [AuthErrorCode.SESSION_EXPIRED]: {
    message: '您的会话已过期。',
    recoveryGuidance: '请重新登录以继续。'
  },
  [AuthErrorCode.REFRESH_TOKEN_EXPIRED]: {
    message: '您的会话已过期。',
    recoveryGuidance: '请重新登录以继续。'
  },
  [AuthErrorCode.RATE_LIMIT_EXCEEDED]: {
    message: '请求过多。请稍候。',
    recoveryGuidance: '请等待几秒钟后重试。'
  },
  [AuthErrorCode.TOO_MANY_ATTEMPTS]: {
    message: '失败尝试次数过多。',
    recoveryGuidance: '请等待5分钟后重试。'
  },
  [AuthErrorCode.NETWORK_ERROR]: {
    message: '网络错误。请检查您的网络连接。',
    recoveryGuidance: '检查您的连接并重试。'
  },
  [AuthErrorCode.SERVER_ERROR]: {
    message: '服务器错误。请稍后重试。',
    recoveryGuidance: '我们的团队已收到通知。'
  },
  [AuthErrorCode.SERVICE_UNAVAILABLE]: {
    message: '服务暂时不可用。',
    recoveryGuidance: '请几分钟后重试。'
  },
  [AuthErrorCode.TIMEOUT]: {
    message: '请求超时。请重试。',
    recoveryGuidance: '检查您的连接并重试。'
  },
  [AuthErrorCode.VALIDATION_ERROR]: {
    message: '验证错误。请检查您的输入。',
    recoveryGuidance: '检查突出显示的字段并更正错误。'
  },
  [AuthErrorCode.INVALID_INPUT]: {
    message: '输入无效。请检查您的数据。',
    recoveryGuidance: '检查您的输入并重试。'
  },
  [AuthErrorCode.RESET_TOKEN_EXPIRED]: {
    message: '密码重置链接已过期。',
    recoveryGuidance: '请请求新的链接。'
  },
  [AuthErrorCode.RESET_TOKEN_INVALID]: {
    message: '密码重置链接无效。',
    recoveryGuidance: '请请求新的链接。'
  },
  [AuthErrorCode.PASSWORD_RECENTLY_USED]: {
    message: '此密码最近已使用过。',
    recoveryGuidance: '出于安全原因，您不能重复使用最近的密码。'
  },
  [AuthErrorCode.UNKNOWN]: {
    message: '发生意外错误。',
    recoveryGuidance: '请重试。如果问题持续存在，请联系支持。'
  },
  [AuthErrorCode.PERMISSION_DENIED]: {
    message: '您没有执行此操作的权限。',
    recoveryGuidance: '如果您认为这是一个错误，请联系您的管理员。'
  }
};

/**
 * Japanese translations
 */
const JA_TRANSLATIONS: AuthErrorTranslationMap = {
  [AuthErrorCode.INVALID_CREDENTIALS]: {
    message: 'メールアドレスまたはパスワードが無効です。',
    recoveryGuidance: 'Caps Lockがオフになっていることを確認してください。'
  },
  [AuthErrorCode.INVALID_EMAIL]: {
    message: '有効なメールアドレスを入力してください。'
  },
  [AuthErrorCode.INVALID_PASSWORD]: {
    message: 'パスワードが無効です。もう一度お試しください。'
  },
  [AuthErrorCode.WEAK_PASSWORD]: {
    message: 'パスワードが弱すぎます。',
    recoveryGuidance: '大文字、小文字、数字、特殊文字を含む8文字以上を使用してください。'
  },
  [AuthErrorCode.ACCOUNT_LOCKED]: {
    message: 'ログイン試行の失敗が多すぎるため、アカウントが一時的にロックされています。',
    recoveryGuidance: '後でもう一度お試しいただくか、サポートにお問い合わせください。'
  },
  [AuthErrorCode.ACCOUNT_DISABLED]: {
    message: 'アカウントが無効になっています。',
    recoveryGuidance: 'サポートにお問い合わせください。'
  },
  [AuthErrorCode.ACCOUNT_NOT_FOUND]: {
    message: 'メールアドレスまたはパスワードが無効です。'
  },
  [AuthErrorCode.ACCOUNT_NOT_VERIFIED]: {
    message: 'ログインする前にメールアドレスを確認してください。',
    recoveryGuidance: '受信トレイで確認リンクを確認してください。'
  },
  [AuthErrorCode.EMAIL_ALREADY_EXISTS]: {
    message: 'このメールアドレスのアカウントは既に存在します。',
    recoveryGuidance: 'ログインするか、別のメールアドレスを使用してください。'
  },
  [AuthErrorCode.USERNAME_ALREADY_EXISTS]: {
    message: 'このユーザー名は既に使用されています。',
    recoveryGuidance: '別のユーザー名を選択してください。'
  },
  [AuthErrorCode.MFA_REQUIRED]: {
    message: '多要素認証が必要です。',
    recoveryGuidance: '確認コードを入力してください。'
  },
  [AuthErrorCode.MFA_INVALID_CODE]: {
    message: '確認コードが無効です。',
    recoveryGuidance: '最新のコードを使用していることを確認してください。'
  },
  [AuthErrorCode.MFA_CODE_EXPIRED]: {
    message: '確認コードの有効期限が切れました。',
    recoveryGuidance: '新しいコードをリクエストしてください。'
  },
  [AuthErrorCode.MFA_TOO_MANY_ATTEMPTS]: {
    message: '確認の試行回数が多すぎます。',
    recoveryGuidance: '新しいコードをリクエストする前に5分間お待ちください。'
  },
  [AuthErrorCode.MFA_NOT_CONFIGURED]: {
    message: '多要素認証が設定されていません。',
    recoveryGuidance: 'アカウント設定でMFAを設定してください。'
  },
  [AuthErrorCode.BACKUP_CODE_INVALID]: {
    message: 'バックアップコードが無効です。',
    recoveryGuidance: 'コードを確認して、もう一度お試しください。'
  },
  [AuthErrorCode.TOKEN_EXPIRED]: {
    message: 'セッションの有効期限が切れました。',
    recoveryGuidance: '続行するには再度ログインしてください。'
  },
  [AuthErrorCode.TOKEN_INVALID]: {
    message: '認証トークンが無効です。',
    recoveryGuidance: '再度ログインしてください。'
  },
  [AuthErrorCode.SESSION_EXPIRED]: {
    message: 'セッションの有効期限が切れました。',
    recoveryGuidance: '続行するには再度ログインしてください。'
  },
  [AuthErrorCode.REFRESH_TOKEN_EXPIRED]: {
    message: 'セッションの有効期限が切れました。',
    recoveryGuidance: '続行するには再度ログインしてください。'
  },
  [AuthErrorCode.RATE_LIMIT_EXCEEDED]: {
    message: 'リクエストが多すぎます。お待ちください。',
    recoveryGuidance: '数秒後に再試行してください。'
  },
  [AuthErrorCode.TOO_MANY_ATTEMPTS]: {
    message: '失敗した試行が多すぎます。',
    recoveryGuidance: '5分後に再試行してください。'
  },
  [AuthErrorCode.NETWORK_ERROR]: {
    message: 'ネットワークエラー。インターネット接続を確認してください。',
    recoveryGuidance: '接続を確認して再試行してください。'
  },
  [AuthErrorCode.SERVER_ERROR]: {
    message: 'サーバーエラー。後でもう一度お試しください。',
    recoveryGuidance: '当社のチームに通知されました。'
  },
  [AuthErrorCode.SERVICE_UNAVAILABLE]: {
    message: 'サービスは一時的に利用できません。',
    recoveryGuidance: '数分後に再試行してください。'
  },
  [AuthErrorCode.TIMEOUT]: {
    message: 'リクエストがタイムアウトしました。もう一度お試しください。',
    recoveryGuidance: '接続を確認して再試行してください。'
  },
  [AuthErrorCode.VALIDATION_ERROR]: {
    message: '検証エラー。入力を確認してください。',
    recoveryGuidance: '強調表示されたフィールドを確認し、エラーを修正してください。'
  },
  [AuthErrorCode.INVALID_INPUT]: {
    message: '無効な入力。データを確認してください。',
    recoveryGuidance: '入力を確認して再試行してください。'
  },
  [AuthErrorCode.RESET_TOKEN_EXPIRED]: {
    message: 'パスワードリセットリンクの有効期限が切れました。',
    recoveryGuidance: '新しいリンクをリクエストしてください。'
  },
  [AuthErrorCode.RESET_TOKEN_INVALID]: {
    message: 'パスワードリセットリンクが無効です。',
    recoveryGuidance: '新しいリンクをリクエストしてください。'
  },
  [AuthErrorCode.PASSWORD_RECENTLY_USED]: {
    message: 'このパスワードは最近使用されました。',
    recoveryGuidance: 'セキュリティ上の理由から、最近のパスワードは再利用できません。'
  },
  [AuthErrorCode.UNKNOWN]: {
    message: '予期しないエラーが発生しました。',
    recoveryGuidance: 'もう一度お試しください。問題が解決しない場合は、サポートにお問い合わせください。'
  },
  [AuthErrorCode.PERMISSION_DENIED]: {
    message: 'このアクションを実行する権限がありません。',
    recoveryGuidance: 'これがエラーだと思われる場合は、管理者に連絡してください。'
  }
};

/**
 * All translations map
 */
export const AUTH_ERROR_TRANSLATIONS: Record<AuthErrorLocale, AuthErrorTranslationMap> = {
  en: EN_TRANSLATIONS,
  es: ES_TRANSLATIONS,
  de: DE_TRANSLATIONS,
  fr: FR_TRANSLATIONS,
  zh: ZH_TRANSLATIONS,
  ja: JA_TRANSLATIONS
};

/**
 * Get localized error message for a given error code
 * Falls back to English if locale not found
 */
export function getLocalizedAuthError(
  code: AuthErrorCode,
  locale: AuthErrorLocale = DEFAULT_AUTH_ERROR_LOCALE
): LocalizedAuthError {
  const translations = AUTH_ERROR_TRANSLATIONS[locale] || AUTH_ERROR_TRANSLATIONS.en;
  return translations[code] || translations[AuthErrorCode.UNKNOWN];
}

/**
 * Get all available locales
 */
export function getAvailableAuthErrorLocales(): AuthErrorLocale[] {
  return Object.keys(AUTH_ERROR_TRANSLATIONS) as AuthErrorLocale[];
}
